import {
  HttpHandlerFn,
  HttpHeaderResponse,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MibToastService } from '@medinbox/angular';
import { catchError, Observable, switchMap, take, throwError } from 'rxjs';
import { OAuthService } from '../services/oauth.service';
import { environment } from '../../../environments/environment';

export function authInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<
  | HttpSentEvent
  | HttpHeaderResponse
  | HttpProgressEvent
  | HttpResponse<unknown>
  | HttpUserEvent<unknown>
> {
  const authService = inject(OAuthService);
  const router = inject(Router);
  const toastService = inject(MibToastService);

  return authService.isLoggedIn().pipe(
    take(1),
    switchMap((isLoggedIn) => {
      if (isLoggedIn && req.url.startsWith(environment.apiUrl)) {
        return authService.getAccessToken().pipe(
          switchMap((token) => {
            const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
            return next(authReq).pipe(
              catchError((error) => {
                // Handle 401 error - attempt token refresh and retry
                if (error.status === 401) {
                  // For authenticated users, try to refresh the token and retry the request
                  return authService.refreshAccessToken().pipe(
                    switchMap((refreshedToken) => {
                      const retryReq = req.clone({
                        setHeaders: { Authorization: `Bearer ${refreshedToken}` },
                      });
                      return next(retryReq);
                    }),
                    catchError((refreshError) => {
                      console.error('Token refresh failed:', refreshError);
                      toastService.show({
                        message: 'Your session has expired. Please log in again.',
                        type: 'warning',
                      });
                      router.navigateByUrl('/logout');
                      return throwError(() => error);
                    }),
                  );
                }
                return throwError(() => error);
              }),
            );
          }),
        );
      }
      return next(req);
    }),
    catchError((error) => {
      return throwError(() => error);
    }),
  );
}
